import { useState, useContext } from "react";
import { Link, useLocation } from "wouter";
import { AuthContext } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Menu, ChevronDown } from "lucide-react";

// Primary navigation items that will always be shown in desktop view
const primaryNavItems = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About Us" },
  { href: "/programs", label: "Programs" },
  { href: "/hire-from-us", label: "Hire" },
  { href: "/research", label: "Research" },
  { href: "/economic-impact-simulator", label: "Impact Sims" },
  { href: "/success-stories", label: "Success Stories" },
  { href: "/contact", label: "Contact" },
];

// All navigation items for mobile view and more dropdown
const navItems = [
  ...primaryNavItems,
  { href: "/get-help", label: "Get Help" },
  { href: "/career-coach", label: "Career Coach" },
  { href: "/get-involved", label: "Get Involved" },
];

export const Navbar = () => {
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  
  // Safely check if AuthContext is available (will be null outside of AuthProvider)
  const authContext = useContext(AuthContext);
  const user = authContext?.user || null;
  const logoutMutation = authContext?.logoutMutation;

  const handleLogout = () => {
    if (logoutMutation) {
      logoutMutation.mutate();
    }
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              {/* Logo */}
              <Link href="/">
                <div className="flex items-center gap-3 cursor-pointer">
                  <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center shadow-md p-1">
                    <svg viewBox="0 0 24 24" className="h-7 w-7" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6A4.997 4.997 0 0 1 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z" 
                        fill="#FACC15" />
                    </svg>
                  </div>
                  <div className="flex flex-col leading-none">
                    <span className="text-xl font-black text-primary-700 tracking-tight">StaffAbility</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Building Better Futures</span>
                  </div>
                </div>
              </Link>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden sm:ml-8 sm:flex sm:items-center">
              {primaryNavItems.map((item) => (
                <Link href={item.href} key={item.href}>
                  <span
                    className={`${
                      location === item.href
                        ? "border-primary-500 text-slate-900 font-semibold"
                        : "border-transparent text-slate-600 hover:border-slate-300 hover:text-slate-800"
                    } inline-flex items-center px-4 pt-1 border-b-2 text-base lg:text-lg cursor-pointer h-16 transition-colors duration-200`}
                  >
                    {item.label}
                  </span>
                </Link>
              ))}
              
              {/* More Dropdown */}
              <div className="relative h-16 flex items-center">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="inline-flex items-center px-4 pt-1 border-b-2 border-transparent text-base lg:text-lg text-slate-600 hover:border-slate-300 hover:text-slate-800 h-16 transition-colors duration-200">
                      More <ChevronDown className="h-4 w-4 ml-1 mt-1" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {navItems.slice(primaryNavItems.length).map((item) => (
                      <Link href={item.href} key={item.href}>
                        <DropdownMenuItem 
                          className={`cursor-pointer ${location === item.href ? "text-primary-600 font-medium" : ""}`}
                        >
                          {item.label}
                        </DropdownMenuItem>
                      </Link>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
          
          {/* Login/Dashboard Button */}
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            {user ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  disabled={logoutMutation?.isPending}
                >
                  {logoutMutation?.isPending ? "Logging out..." : "Logout"}
                </Button>
                <Link href="/dashboard">
                  <Button>Dashboard</Button>
                </Link>
              </div>
            ) : (
              <Link href="/auth">
                <Button>Client Portal</Button>
              </Link>
            )}
          </div>
          
          {/* Mobile Menu Button */}
          <div className="flex items-center sm:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10">
                  <Menu className="h-6 w-6" />
                  <span className="sr-only">Open main menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[250px] sm:w-[300px]">
                <SheetHeader>
                  <SheetTitle>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center shadow-md p-1">
                        <svg viewBox="0 0 24 24" className="h-6 w-6" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6A4.997 4.997 0 0 1 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z" 
                            fill="#FACC15" />
                        </svg>
                      </div>
                      <div className="flex flex-col leading-none">
                        <span className="text-lg font-black text-primary-700 tracking-tight">StaffAbility</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Building Better Futures</span>
                      </div>
                    </div>
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-6 flex flex-col space-y-4">
                  {navItems.map((item) => (
                    <Link href={item.href} key={item.href}>
                      <span
                        className={`${
                          location === item.href
                            ? "text-primary-600 font-medium"
                            : "text-slate-600"
                        } px-2 py-1 rounded-md hover:bg-slate-100 block cursor-pointer`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.label}
                      </span>
                    </Link>
                  ))}
                  
                  <div className="pt-4 border-t border-slate-200">
                    {user ? (
                      <>
                        <Link href="/dashboard">
                          <span
                            className="block px-2 py-1 mb-2 text-primary-600 font-medium cursor-pointer"
                            onClick={() => setIsOpen(false)}
                          >
                            Dashboard
                          </span>
                        </Link>
                        <Button
                          variant="outline"
                          onClick={() => {
                            handleLogout();
                            setIsOpen(false);
                          }}
                          disabled={logoutMutation?.isPending}
                          className="w-full"
                        >
                          {logoutMutation?.isPending ? "Logging out..." : "Logout"}
                        </Button>
                      </>
                    ) : (
                      <Link href="/auth">
                        <Button
                          className="w-full"
                          onClick={() => setIsOpen(false)}
                        >
                          Client Portal
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
